import { CaretLeft, FileImage, Image } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useIDUpload from "../../../hooks/useIDUpload";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { HashLoader } from "react-spinners";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { AnimatePresence, motion } from "framer-motion";
import { leaveSetupTask } from "../../../utils/setupTaskReturn";

const schema = Yup.object().shape({
  compliance_type_1: Yup.string().required("Please select an ID type"),
});

const IDVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const { refetch } = useAuth();

  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  const { uploadID, uploadingID, isIDUploadError, idUploadError } = useIDUpload(
    {
      onSuccess() {
        toast.success("ID uploaded successfully");
        refetch();
        if (fromSetup) leaveSetupTask(navigate, location.state);
      else navigate("/home/settings/compliance");
      },
      onError() {
        toast.success("An error occured uploading ID");
        refetch();
      },
    }
  );

  const handleSubmit = (values: { compliance_type_1: string }) => {
    console.log({
      compliance_type_1: values.compliance_type_1,
      compliance_image_1: {
        file: file as File,
        name: file?.name,
      },
    });

    uploadID({
      compliance_type_1: values.compliance_type_1,
      compliance_image_1: {
        file: file as File,
        name: file?.name,
      },
    });
  };

  return (
    <>
      {uploadingID && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        validationSchema={schema}
        initialValues={{
          compliance_type_1: "",
        }}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid }) => {
          return (
            <Form className="flex items-center justify-center gap-10 md:px-5 md:py-5 pb-20 flex-col">
              <div className="flex-col gap-4 flex items-start justify-center w-full max-w-[32rem]">
                <div className="bg-white p-8 border border-gray-200 rounded-xl w-full flex flex-col gap-5">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start"
                  >
                    <CaretLeft weight="bold" size={20} />
                  </button>

                  <div className="flex flex-col gap-2">
                    <h1 className="font-medium text-3xl">Upload your ID</h1>
                    <div className="text-gray-800">
                      Please upload a clear photo of your valid ID (Voter's
                      Card, NIN Slip, Driver's License or Passport)
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label
                      htmlFor="employment_status"
                      className="text-sm font-bold text-gray-700"
                    >
                      What type of ID do you have?
                    </label>
                    <Field
                      as="select"
                      id="compliance_type_1"
                      name="compliance_type_1"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value="NIN Slip">NIN Slip</option>
                      <option value="Passport">Passport</option>
                      <option value="Driver's License">Driver's License</option>
                      <option value="Voter's Card">Voter's Card</option>
                    </Field>
                    <ErrorMessage
                      name="compliance_type_1"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={ref}
                    className="hidden"
                  />

                  <AnimatePresence>
                    {file ? (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center gap-3 border border-gray-300 p-4 rounded-lg"
                      >
                        <div>
                          <FileImage size={28} />
                        </div>
                        <div className="flex flex-col flex-1">
                          <div className="text-sm font-medium text-gray-800">
                            {file.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Size: {(file.size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setFile(null);
                          }}
                          type="button"
                          className="text-sm hover:text-red-400 cursor-pointer transition-all"
                        >
                          Remove
                        </button>
                      </motion.div>
                    ) : (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        type="button"
                        onClick={() => {
                          ref.current?.click();
                        }}
                        className="flex p-4 border border-gray-300 rounded-lg flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 bg-gray-100 p-4 rounded-md">
                          <Image size={32} />
                        </div>
                        <div className="font-medium text-lg">Choose File</div>
                        <div className="text-sm">
                          JPEG, JPG, PNG, PDF max 5MB
                        </div>
                      </motion.button>
                    )}
                  </AnimatePresence>
                  <ul className="flex flex-col gap-2 list-disc list-inside text-gray-800">
                    <li>Make sure all information is visible</li>
                    <li>Capture all corners of the ID card</li>
                    <li>Avoid glare or blurriness</li>
                  </ul>
                  {isIDUploadError && (
                    <div className="text-red-500">{idUploadError?.message}</div>
                  )}
                  <div className="w-full flex gap-5">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                    >
                      <CaretLeft weight="bold" size={20} />
                      <span>Back</span>
                    </button>

                    <button
                      disabled={!dirty || !isValid || uploadingID || !file}
                      type="submit"
                      className="p-2 py-3 text-center flex-1 bg-deepBlue text-white px-10 border border-deepBlue rounded-xl font-medium w-full"
                    >
                      {uploadingID ? "Uploading..." : "Upload ID"}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default IDVerification;
